import { z } from 'zod';

export const KycStatus = z.enum(['pending', 'verified', 'rejected']);
export type KycStatusType = z.infer<typeof KycStatus>;

/**
 * Identity documents a customer can submit from the app. Mirrors `_ID_TYPES`
 * in offerings-service, which validates the value on submission.
 *
 * `.catch()` rather than a strict parse: this is presentation vocabulary the
 * reviewer reads, and a document type we have not shipped a label for yet must
 * not blank out the review panel that shows the ID number next to it.
 */
export const IdType = z
  .enum(['national_id', 'passport', 'drivers_license', 'voter_id'])
  .catch('national_id');
export type IdTypeValue = z.infer<typeof IdType>;

export const ID_TYPE_LABELS: Record<IdTypeValue, string> = {
  national_id: 'National ID',
  passport: 'Passport',
  drivers_license: "Driver's licence",
  voter_id: 'Voter ID',
};

/**
 * Where an identity submission stands, from the REVIEWER's point of view.
 *
 * Four states out of a three-label enum, because `pending` means two different
 * things: nobody has submitted anything, or a customer's documents are sitting
 * in the queue waiting on us. `kycSubmittedAt` is what separates them, and the
 * difference is the whole job — one is a customer to chase, the other is work
 * on our desk.
 */
export type KycReviewState =
  | 'notSubmitted'
  | 'awaitingReview'
  | 'verified'
  | 'rejected';

export function kycReviewState(customer: {
  kycStatus: KycStatusType;
  kycSubmittedAt: string | null;
}): KycReviewState {
  if (customer.kycStatus === 'verified') return 'verified';
  if (customer.kycStatus === 'rejected') return 'rejected';
  return customer.kycSubmittedAt ? 'awaitingReview' : 'notSubmitted';
}

export const CreditTier = z.enum(['A', 'B', 'C', 'D', 'E']);
export type CreditTierType = z.infer<typeof CreditTier>;

/**
 * One rung of the credit tier ladder, exactly as cs-engine serves it. The
 * portal renders these — it never re-derives a customer's tier from the
 * score bands, which is cs-engine's job alone.
 */
export const CreditTierInfo = z.object({
  tier: z.string(),
  name: z.string(),
  minScore: z.number(),
  maxScore: z.number(),
  creditLimit: z.number(),
  markupRate: z.number(),
  maxTermDays: z.number(),
});
export type CreditTierInfoModel = z.infer<typeof CreditTierInfo>;

export const GenderSchema = z.enum(['male', 'female']);

export const Customer = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  phoneNumber: z.string(),
  email: z.string().email().nullable(),
  nationalId: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  gender: GenderSchema.nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  region: z.string().nullable(),
  kycStatus: KycStatus,
  kycVerifiedAt: z.string().nullable(),
  // "ZAAD KYC" when the ZaadCredit subscriber lookup auto-verified, otherwise
  // the branch operator who verified by hand. Optional: older records predate it.
  kycVerifiedBy: z.string().nullable().optional(),
  // Customer-submitted-KYC evidence. All nullable: a branch-registered customer
  // who never opened the app has none of it, and that is a legitimate state to
  // render — not a missing field.
  kycSubmittedAt: z.string().nullable(),
  kycRejectionReason: z.string().nullable(),
  idType: IdType.nullable(),
  idPhotoUrl: z.string().nullable(),
  // The live selfie from app onboarding, compared against the document photo.
  profileImage: z.string().nullable(),
  creditScore: z.number().nullable(),
  creditTier: CreditTier.nullable(),
  scoringStatus: z
    .enum(['pending', 'in_progress', 'complete', 'failed'])
    .nullable()
    .optional(),
  availableCredit: z.number().nullable().optional(),
  activeContracts: z.number(),
  totalDevicesFinanced: z.number(),
  branchId: z.string().nullable(),
  branchName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
});
export type CustomerModel = z.infer<typeof Customer>;

export const CustomerListItem = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  phoneNumber: z.string(),
  kycStatus: KycStatus,
  // Lets the list tell a submission waiting on us from a customer who never
  // started — both of which the enum calls `pending`.
  kycSubmittedAt: z.string().nullable(),
  creditTier: CreditTier.nullable(),
  activeContracts: z.number(),
  createdAt: z.string(),
});
export type CustomerListItemModel = z.infer<typeof CustomerListItem>;

export const ConsentViaSchema = z.enum(['branch', 'app', 'ussd']);
export type ConsentVia = z.infer<typeof ConsentViaSchema>;

export const ScoringStatusSchema = z.object({
  status: z
    .enum(['pending', 'in_progress', 'complete', 'failed'])
    .nullable()
    .optional(),
  updatedAt: z.string().nullable().optional(),
  attempts: z.number().nullable().optional(),
  lastError: z.string().nullable().optional(),
  failureReason: z.string().nullable().optional(),
});
export type ScoringStatusModel = z.infer<typeof ScoringStatusSchema>;

/**
 * Subset of the backend EligibilityResponse the detail page consumes.
 * Fetched separately from the customer record because `availableCredit`
 * is computed on the fly (tier credit limit − outstanding balance) and
 * does not survive a round-trip to the customer row.
 */
export const CustomerEligibility = z.object({
  eligible: z.boolean(),
  creditScore: z.number().nullable().optional(),
  creditTier: CreditTier.nullable().optional(),
  availableCredit: z.number().nullable().optional(),
  // Scoring lifecycle from cs-engine via loan-service. Without these the portal
  // cannot tell "still scoring" from "scoring gave up" — see eligibilityPolling.
  scoringStatus: z.string().nullable().optional(),
  scoringFailureReason: z.string().nullable().optional(),
  // The ladder cs-engine serves alongside the verdict, so the portal shows
  // tier names and score bands without keeping its own copy.
  tiers: z.array(CreditTierInfo).optional().default([]),
});
export type CustomerEligibilityModel = z.infer<typeof CustomerEligibility>;

/**
 * Registered subscriber identity returned by the ZAAD KYC integration, used
 * to pre-fill the onboarding form. Mirrors the backend ZaadKycLookupResponse:
 * the name is all the telco returns. `found = false` means the agent enters
 * details manually, with `reason` carrying the telco's verdict when it gave one.
 */
export const ZaadKycLookup = z.object({
  found: z.boolean(),
  msisdn: z.string(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  fullName: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  source: z.string().optional(),
});
export type ZaadKycLookupModel = z.infer<typeof ZaadKycLookup>;

export const CreateCustomer = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  phoneNumber: z.string().min(9),
  email: z.string().email().optional().or(z.literal('')),
  nationalId: z.string().optional(),
  dateOfBirth: z.string().optional(),
  gender: GenderSchema.optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  region: z.string().optional(),
  branchId: z.string().optional(),
  tenantId: z.string().optional(),
  // Form-level required tick. The wire payload uses ``consent: { version,
  // acceptedVia }`` instead (see api.ts:mapCreatePayload); this field is
  // an internal control on the form and gets translated, not forwarded.
  consentAccepted: z.boolean().optional(),
  // True when the form was pre-filled from a ZAAD (Telesom) KYC record.
  // Forwarded to the backend so it can confirm the subscriber identity and
  // auto-verify KYC, skipping the manual branch verification step.
  kycSourcedFromZaad: z.boolean().optional(),
});
export type CreateCustomerModel = z.infer<typeof CreateCustomer>;
